import { useMutation, useQuery, useInfiniteQuery } from "@tanstack/react-query";
import React, { useState, useRef } from "react";
import { Dropdown, Modal, Form, ListGroup, Button } from "react-bootstrap";
import { useEffect } from 'react';
import {
  FaKey,
  FaUser,
  FaStickyNote,
  FaPlus,
  FaTrash,
  FaSpinner,
  FaEdit,
  FaTimes
} from "react-icons/fa";
import { FiLogOut } from "react-icons/fi";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import * as images from "../assets/image";
import useDetails from "../hooks/useDetails";
import { toggleSwitch } from "../redux/features/authSlice";
import useRole from "../hooks/useRole";
import { login } from "../redux/features/authSlice";
import {
  getNotificationCount,
  logOut,
  getNotes,
  addNotes,
  deleteNotes,
  updateNotes
} from "../services/services";
import { constant } from "../utils/constants";
import "../styles/navbar.css";

const Navbar = () => {
  const details = useDetails();
  const navigate = useNavigate();
  const [sidebarToggle, setSidebarToggle] = useState(false);
  const role = useRole();
  const dispatch = useDispatch();
  const location = useLocation();
  const [notificationCount, setNotificationCount] = useState(0);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [showNotesPanel, setShowNotesPanel] = useState(false);
  const [notesText, setNotesText] = useState("");
  const [showError, setShowError] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const notesContainerRef = useRef(null);

  // Fetch notes with pagination
  const {
    data: notesData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch: refetchNotes,
    isLoading: isLoadingNotes,
  } = useInfiniteQuery({
    queryKey: ["notes"],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await getNotes({
        page: pageParam,
        limit: 100,
        sortBy: "DESC",
      });
      return {
        notes: response.data.data.notes || [],
        total: response.data.data.total,
        current_page: response.data.data.current_page,
        total_pages: response.data.data.total_pages,
        nextPage:
          response.data.data.current_page < response.data.data.total_pages
            ? response.data.data.current_page + 1
            : undefined,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 1,
  });

  // Flatten notes from all pages
  const notesList = notesData?.pages.flatMap((page) => page.notes) || [];

  // Add note mutation
  const addNoteMutation = useMutation({
    mutationFn: (noteText) => addNotes({ note: noteText?.trim() }),
    onSuccess: () => {
      refetchNotes();
      setShowNotesModal(false);
      setShowError(false);
      setNotesText("");
    },
    onError: (error) => {
      console.error("Error adding note:", error);
    },
  });

  // Delete note mutation
  const deleteNoteMutation = useMutation({
    mutationFn: (noteId) => deleteNotes(noteId),
    onSuccess: () => {
      refetchNotes();
    },
    onError: (error) => {
      console.error("Error deleting note:", error);
    },
  });

  // Update note mutation
  const updateNoteMutation = useMutation({
    mutationFn: ({ noteId, noteText }) => updateNotes(noteId, { note: noteText.trim() }),
    onSuccess: () => {
      refetchNotes();
      setShowNotesModal(false);
      setEditingNoteId(null);
      setShowError(false);
      setNotesText("");
    },
    onError: (error) => {
      console.error("Error updating note:", error);
    },
  });

  const handleNotificationBtn = () => {
    if (role === constant.ROLE.ADMIN || role === constant.ROLE.EMPLOYEE) {
      navigate(`/${role}/notifications`);
    } else {
      navigate(`/notifications`);
    }
  };

  const handleSidebarToggle = () => {
    dispatch(toggleSwitch());
  };

  const logoutMutation = useMutation({
    mutationFn: () => logOut(),
    onSuccess: () => {
      dispatch(login({}));
    },
  });

  const handleAddNote = () => {
    setShowNotesModal(true);
    setShowError(false);
    setEditingNoteId(null);
  };

  const handleCloseNotesModal = () => {
    setShowNotesModal(false);
    setNotesText("");
    setShowError(false);
    setEditingNoteId(null);
  };

  const handleSaveNote = () => {
    if (!notesText.trim()) {
      setShowError(true);
      return;
    }
    if (editingNoteId) {
      updateNoteMutation.mutate({ noteId: editingNoteId, noteText: notesText });
    } else {
      addNoteMutation.mutate(notesText);
    }
  };

  const handleDeleteNote = (_id) => {
    deleteNoteMutation.mutate(_id);
  };

  const handleEditNote = (note) => {
    setEditingNoteId(note._id);
    setNotesText(note.note);
    setShowNotesModal(true);
  };

  const toggleNotesPanel = () => {
    setShowNotesPanel(!showNotesPanel);
    if (!showNotesPanel) {
      refetchNotes();
    }
  };

  const handleScroll = () => {
    if (notesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        notesContainerRef.current;
      const scrollPosition = scrollTop + clientHeight;

      // Closer to bottom: within 100px
      const threshold = 100;

      if (
        scrollHeight - scrollPosition < threshold &&
        hasNextPage &&
        !isFetchingNextPage &&
        !isLoadingNotes
      ) {
        fetchNextPage();
      }
    }
  };

  const { data = 0, refetch } = useQuery({
    queryKey: ["notification-count", location.pathname],
    queryFn: async () => {
      let resp = await getNotificationCount();
      setNotificationCount(resp?.data?.data?.unreadRecords);
      return resp?.data?.data?.unreadRecords ?? 0;
    },
  });

  const useOutsideClick = (ref, callback, condition = true) => {
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (condition && ref.current && !ref.current.contains(event.target)) {
          callback();
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [ref, callback, condition]);
  };

  const notesPanelRef = useRef(null);

  // Use the outside click hook with condition
  useOutsideClick(notesPanelRef, () => {
    setShowNotesPanel(false);
  }, !showNotesModal); // Only active when modal is not open


  return (
    <>
      <header className={"header"}>
        <h3
          className="headerHeading mb-0"
          onClick={() => {
            handleSidebarToggle();
          }}
        >
          <i className="fa-solid fa-bars menuToggle"></i>
        </h3>

        <div className="headerRight">
          {/* Notification Button */}
          <div
            className="navNotification position-relative me-3"
            onClick={handleNotificationBtn}
          >
            <img
              src={images.notificationIcon}
              alt="image"
              className="img-fluid"
            />
            {notificationCount !== 0 && (
              <div className="notificationBadge">{notificationCount || 0}</div>
            )}
          </div>

          {/* Notes Button */}
       {role == constant.ROLE.ADMIN?   <div
            className="navNotification position-relative me-3"
            onClick={toggleNotesPanel}
            title="Quick Notes"
          >
            <FaStickyNote size={20} color="white" />
          </div>:''}

          <div className="profileArea">
            <Dropdown className="profileDropdown">
              <Dropdown.Toggle id="dropdown-basic">
                <span className="img-ic">
                  <img
                    src={
                      details?.profilePhoto
                        ? details?.profilePhoto
                        : images.profile
                    }
                    alt="Image"
                    className="me-3 profileImg"
                  ></img>
                </span>
                <label className="text-capitalize">{`${details?.firstName} ${details?.lastName}`}</label>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item
                  onClick={() =>
                    navigate(
                      role == constant.ROLE.ADMIN
                        ? "/admin/profile"
                        : role == constant.ROLE.EMPLOYEE
                        ? "/employee/profile"
                        : "/profile"
                    )
                  }
                >
                  <FaUser className="me-2" />
                  Profile
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() =>
                    navigate(
                      role == constant.ROLE.ADMIN
                        ? "/admin/change-password"
                        : role == constant.ROLE.EMPLOYEE
                        ? "/employee/change-password"
                        : "/change-password"
                    )
                  }
                >
                  <FaKey className="me-2" />
                  Change Password
                </Dropdown.Item>

                <Dropdown.Item
                  onClick={(e) => {
                    e.preventDefault();
                    logoutMutation.mutate();
                  }}
                >
                  <FiLogOut className="me-2" />
                  Logout
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        {/* Notes Panel */}
        {showNotesPanel && (
          <div className="notes-panel" ref={notesPanelRef}>
            <div className="notes-panel-header">
              <h5>Quick Notes</h5>
              <button
                className="close-btn"
                onClick={() => setShowNotesPanel(false)}
              >
                &times;
              </button>
            </div>
            <Button
              variant="primary"
              className="add-note-btn"
              onClick={handleAddNote}
              disabled={addNoteMutation.isPending}
            >
              {addNoteMutation.isPending ? (
                <FaSpinner className="me-2 spin" />
              ) : (
                <FaPlus className="me-2" />
              )}
              Add Note
            </Button>
            <div
              className="notes-list-container"
              onScroll={handleScroll}
              ref={notesContainerRef}
            >
              <ListGroup className="notes-list">
                {notesList.map((note) => (
                  <ListGroup.Item key={note._id} className="note-item">
                    <div className="note-content">
                      <p>{note.note}</p>
                    </div>
                    <div className="note-actions">
                    
                      <Button
                        variant="link"
                        className="delete-note p-0"
                        onClick={() => handleDeleteNote(note._id)}
                        disabled={
                          deleteNoteMutation.isPending ||
                          updateNoteMutation.isPending
                        }
                      >
                        {deleteNoteMutation.isPending &&
                        deleteNoteMutation.variables === note._id ? (
                          <FaSpinner className="spin" />
                        ) : (
                          <FaTrash />
                        )}
                      </Button>
                      <Button
                        variant="link"
                        className="edit-note p-0 ms-2"
                        onClick={() => handleEditNote(note)}
                        disabled={
                          deleteNoteMutation.isPending ||
                          updateNoteMutation.isPending
                        }
                      >
                        <FaEdit style={{fontSize:'18px',color:'var(--blue)'}} />
                      </Button>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>

              {/* Loader or Empty States */}
              {isLoadingNotes && notesList.length === 0 && (
                <div className="text-center py-3 text-muted">
                  <FaSpinner className="spin" /> Loading notes...
                </div>
              )}

              {!isLoadingNotes && notesList.length === 0 && (
                <p className="no-notes">No notes yet</p>
              )}

              {isFetchingNextPage && (
                <div className="text-center py-2 text-muted">
                  <FaSpinner className="spin" /> Loading more notes...
                </div>
              )}

              {!hasNextPage && notesList.length > 0 && (
                <div className="text-center py-2 text-muted">
                  No more notes to load
                </div>
              )}
            </div>
          </div>
        )}

        {/* Add/Edit Note Modal */}
        <Modal show={showNotesModal} onHide={handleCloseNotesModal}>
          <Modal.Header closeButton>
            <Modal.Title>{editingNoteId ? "Edit Note" : "Add New Note"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group controlId="notesTextarea">
                <Form.Control
                  as="textarea"
                  rows={5}
                  value={notesText}
                  onChange={(e) => setNotesText(e.target.value)}
                  placeholder="Write your note here..."
                  style={{ resize: "none", outline: "none", boxShadow: "none" }}
                  className="no-outline"
                  disabled={addNoteMutation.isPending || updateNoteMutation.isPending}
                />
                {showError && !notesText.trim() && (
                  <Form.Text className="text-danger">
                    Note cannot be empty
                  </Form.Text>
                )}
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={handleCloseNotesModal}
              disabled={addNoteMutation.isPending || updateNoteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              style={{
                backgroundColor: "var(--blue)",
                border: "none",
                height: "38px",
              }}
              onClick={handleSaveNote}
              disabled={addNoteMutation.isPending || updateNoteMutation.isPending}
            >
              {(addNoteMutation.isPending || updateNoteMutation.isPending) ? (
                <FaSpinner className="spin me-2" />
              ) : null}
              {editingNoteId ? "Update Note" : "Save Note"}
            </Button>
          </Modal.Footer>
        </Modal>
      </header>
    </>
  );
};

export default Navbar;